// Admin shell.
//
// The sidebar is permanent from `md` up and a slide-over drawer below it.
// A fixed 14rem column on a phone leaves barely half the screen for the
// page it's meant to be navigating.
import React, { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ExternalLink, Menu, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import logoMark from '../../assets/bleums-mark.png';

const adminNav = [
  { to: '/admin', label: 'Dashboard', end: true },
  { to: '/admin/products', label: 'Products' },
  { to: '/admin/libraries', label: 'Private libraries' },
  { to: '/admin/featured', label: 'Featured items' },
  { to: '/admin/orders', label: 'Orders' },
  { to: '/admin/inquiries', label: 'Inquiries' },
  { to: '/admin/settings', label: 'Settings' },
];

const AdminLayout: React.FC = () => {
  const { logout, state } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);

  // Tapping a link should navigate *and* get the drawer out of the way.
  useEffect(() => setNavOpen(false), [location.pathname]);

  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setNavOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navOpen]);

  // Point "Add to Home Screen" at the admin while you're in it.
  //
  // The real fix is served, not scripted: /admin paths get admin.html,
  // which links the admin manifest in its <head> (see
  // scripts/make-admin-html.js). This only covers the leftover case where
  // the admin was reached by client-side navigation from the public site,
  // so the document in the tab is still index.html and carries the shop's
  // manifest. It cannot be the whole fix on iOS -- Safari resolves the
  // manifest as the page loads, well before React mounts -- which is
  // exactly why swapping it here alone did not work. Restored on the way
  // out so adding a page from the public site still installs the shop.
  useEffect(() => {
    const link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    if (!link) return;
    const shopManifest = link.href;
    link.href = '/admin-manifest.json';
    return () => {
      link.href = shopManifest;
    };
  }, []);

  const sidebar = (
    <>
      <Link to="/admin" className="flex items-center gap-2 mb-6">
        <img src={logoMark} alt="" aria-hidden="true" className="h-8 w-8 object-contain" />
        <span className="font-display text-xl text-primary">Bleums Admin</span>
      </Link>
      <nav className="space-y-1 flex-1">
        {adminNav.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              `block rounded px-3 py-2 text-sm ${
                isActive
                  ? 'bg-primary/10 text-primary font-medium'
                  : 'text-text hover:bg-background-dark'
              }`
            }
          >
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-background-dark pt-4 mt-4">
        {/* Opens in a new tab so the admin session stays put. */}
        <a
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 rounded px-3 py-2 text-sm text-text hover:bg-background-dark"
        >
          <ExternalLink size={14} />
          View public site
        </a>
      </div>

      <div className="border-t border-background-dark pt-4 mt-4 text-xs text-text-light">
        <div className="mb-2">{state.user?.username}</div>
        <button
          onClick={() => {
            logout();
            navigate('/admin/login');
          }}
          className="text-primary hover:underline"
        >
          Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen md:flex">
      {/* Phone header. Sticky so navigation stays reachable down a long
          orders table. */}
      <header className="md:hidden sticky top-0 z-30 flex items-center gap-3 bg-white border-b border-background-dark px-4 h-14">
        <button
          type="button"
          onClick={() => setNavOpen(true)}
          aria-label="Open menu"
          className="p-2 -ml-2 text-text"
        >
          <Menu size={20} />
        </button>
        <Link to="/admin" className="flex items-center gap-2 min-w-0">
          <img src={logoMark} alt="" aria-hidden="true" className="h-7 w-7 object-contain" />
          <span className="font-display text-lg text-primary truncate">Bleums Admin</span>
        </Link>
      </header>

      {navOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/40"
          onClick={() => setNavOpen(false)}
        >
          <aside
            // Stops a tap inside the drawer closing it via the backdrop.
            onClick={e => e.stopPropagation()}
            className="w-64 max-w-[85%] h-full bg-white border-r border-background-dark p-4 flex flex-col overflow-y-auto"
          >
            <button
              type="button"
              onClick={() => setNavOpen(false)}
              aria-label="Close menu"
              className="self-end p-2 -mr-2 -mt-2 text-text-light"
            >
              <X size={18} />
            </button>
            {sidebar}
          </aside>
        </div>
      )}

      <aside className="hidden md:flex w-56 shrink-0 bg-white border-r border-background-dark p-4 flex-col">
        {sidebar}
      </aside>

      {/* min-w-0 lets wide children scroll inside the main column instead
          of stretching the page and scrolling the whole layout sideways. */}
      <main className="flex-1 min-w-0 p-4 sm:p-6 max-w-5xl">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
